import React from "react";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import { overwolfGameInfo } from "@/hooks/useOverwolfInterop";
import { capitalizeFirstLetter } from "@/utils/formatString/capitalizeFirstLetter";
import { FaSignOutAlt, FaTimes, FaTrophy } from "react-icons/fa";

interface GameExitNotificationProps {
    gameInfo: overwolfGameInfo;
    sessionDuration?: number; // in minutes
    xpEarned?: number;
    questsCompleted?: number;
    onExitGameMode: () => void;
    onStayInGameMode: () => void;
    onDismiss: () => void;
}

const GameExitNotification: React.FC<GameExitNotificationProps> = ({
    gameInfo,
    sessionDuration,
    xpEarned,
    questsCompleted,
    onExitGameMode,
    onStayInGameMode,
    onDismiss,
}) => {
    const isOnOverwolf = useSelector((store: IStore) => store.metaStore.isOnOverwolf);

    // Only show for Overwolf users
    if (!isOnOverwolf) {
        return null;
    }

    const formatSessionDuration = (minutes?: number): string => {
        if (!minutes || minutes < 1) return "a few moments";
        if (minutes < 60) return `${Math.round(minutes)} minute${Math.round(minutes) !== 1 ? "s" : ""}`;
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = Math.round(minutes % 60);
        if (remainingMinutes === 0) {
            return `${hours} hour${hours !== 1 ? "s" : ""}`;
        }
        return `${hours}h ${remainingMinutes}m`;
    };

    return (
        <div className="game-notification game-notification--exit">
            <div className="game-notification__content">
                {/* Game Exit Icon */}
                <div className="game-notification__icon">
                    <FaSignOutAlt size={24} />
                </div>

                {/* Message Content */}
                <div className="game-notification__message">
                    <div className="game-notification__title">
                        Game Session Ended
                    </div>
                    <div className="game-notification__subtitle">
                        {capitalizeFirstLetter(gameInfo.gameTitle)} is no longer running.
                        {sessionDuration && ` You played for ${formatSessionDuration(sessionDuration)}.`}
                    </div>

                    {/* Session Stats */}
                    {(xpEarned || questsCompleted) &&
                        <div className="game-notification__stats">
                            {xpEarned && xpEarned > 0 &&
                                <div className="game-notification__stat">
                                    <FaTrophy size={16} />
                                    <span>{xpEarned} XP earned</span>
                                </div>
                            }
                            {questsCompleted && questsCompleted > 0 &&
                                <div className="game-notification__stat">
                                    <FaTrophy size={16} />
                                    <span>{questsCompleted} quest{questsCompleted !== 1 ? "s" : ""} completed</span>
                                </div>
                            }
                        </div>
                    }
                </div>

                {/* Actions */}
                <div className="game-notification__actions">
                    <button
                        className="game-notification__button game-notification__button--primary"
                        onClick={onExitGameMode}
                    >
                        Exit Game Mode
                    </button>
                    <button
                        className="game-notification__button game-notification__button--secondary"
                        onClick={onStayInGameMode}
                    >
                        Stay in Game Mode
                    </button>
                </div>

                {/* Close Button */}
                <button
                    className="game-notification__close"
                    onClick={onDismiss}
                    aria-label="Close notification"
                >
                    <FaTimes size={16} />
                </button>
            </div>
        </div>
    );
};

export default GameExitNotification;